import React from 'react';
import useEditorStore, { EntityRef, LayoutDraft, LayoutEntity } from './editorStore';
import BlockList from './BlockList';

/**
 * Canvas surfaces for LAYOUT entities, each replicating its public shell:
 * - custom page (pageLocation 'header'): apps/app OrganizationPageRenderer's
 *   `.sub-container > .page-title > body` — the page name IS the public URL
 *   slug, edited inline here;
 * - org-home fallback page: OrganizationPage's fallback branch — a plain
 *   `.sub-container` white panel, no title chrome;
 * - division callout: DivisionHeader's blue callout (`bg-blue-50` etc.),
 *   shown above that division's public schedule.
 *
 * All bodies are the same editable BlockList the post cards use.
 */

export type LayoutSurfaceKind = 'page' | 'fallback-home' | 'division-callout';

interface Props {
  layout: LayoutEntity;
  kind: LayoutSurfaceKind;
  leagueName: string;
  contextLabel?: string; // e.g. the division name for callouts
}

export default function LayoutSurface({ layout, kind, leagueName, contextLabel }: Props) {
  const ref: EntityRef = { kind: 'layout', id: layout._id };
  const draft = useEditorStore((s) => s.drafts[`layout:${layout._id}`]) as LayoutDraft | undefined;
  const patchDraft = useEditorStore((s) => s.patchDraft);

  if (!draft) return null; // selecting the surface always drafts it first

  if (kind === 'division-callout') {
    return (
      <div className="canvas-surface">
        <p className="canvas-surface__hint">
          Callout shown above the public schedule for <strong>{contextLabel || 'this division'}</strong>.
          Leave it empty to show nothing.
        </p>
        <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-gray-700">
          <BlockList entity={ref} blocks={draft.blocks} leagueName={leagueName} />
        </div>
      </div>
    );
  }

  return (
    <div className="canvas-surface">
      {kind === 'fallback-home' && (
        <p className="canvas-surface__hint">
          This page shows publicly <strong>only while no posts are published</strong>. New
          organizations start here. Publish a post and the posts feed takes over.
        </p>
      )}
      <div className="canvas-panel">
        {kind === 'page' && (
          <div className="canvas-panel__title">
            <input
              type="text"
              value={draft.name}
              placeholder="Page name"
              onChange={(e) => patchDraft(ref, { name: e.target.value })}
              aria-label="Page name"
            />
            <p className="canvas-surface__hint">
              The name is also the page&apos;s address: /{(draft.name || 'page-name').replace(/\s+/g, '-').toLowerCase()}
            </p>
          </div>
        )}
        <BlockList entity={ref} blocks={draft.blocks} leagueName={leagueName} />
      </div>
    </div>
  );
}
